/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import MainBodyLayout from '../partials/MainBodyLayout';
import Input from '../../../../components/Input';
import { revealPassword } from '../../../Onboarding/helpers';
import { cardSettingsScreens } from '../constants';
import { CardSettingsContext } from '../../../../contexts/card/settings/context';
import { CARD_MONAVATE_SET_PIN } from '../../mutations';
import { showAlert } from '../../../../utilities/notifications';
import { clearFormError } from '../../../../utilities/formsUtils';
import { FLOWS, sendStats } from '../../../../services/mixpanel';
import { MXP_CARD_ORDER_SET_PIN_VISIT } from '../../CardOrder/mixpanel-events';
import { cardProviders } from '../../constants';
import { GenericLoading } from '../../../../components/Placeholders';

const SetPin = () => {
  const { t: translate } = useTranslation();
  const [form, updateForm] = useState({
    pin: '',
    confirmPin: '',
  });
  const history = useHistory();

  const { openScreen, state } = useContext(CardSettingsContext);

  const currentPIN = state?.screenOptions?.currentPin;
  const changePinUrl = state?.screenOptions?.changePinUrl;
  const cardProvider = state?.user?.cardProvider;

  const [pinUrlLoading, setPinUrlLoading] = useState(true);

  // Galileo set pin iframe message
  const handlePgwState = useCallback((message) => {
    try {
      if (message?.origin === process.env.REACT_APP_GALILEO_PIN_IFRAME_ORIGIN) {
        if (message?.data === 0) {
          showAlert({
            message: translate('set_pin_success_msg'),
            type: 'success',
          });
          history.push('/dashboard/card');
        } else if (message?.data === -10) {
          showAlert({
            message: translate('set_pin_session_expired'),
            type: 'danger',
          });
          openScreen(cardSettingsScreens.VERIFY_PASSWORD);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }, []);

  useEffect(() => {
    sendStats(MXP_CARD_ORDER_SET_PIN_VISIT, {}, FLOWS.CARD_MANAGEMENT);
    window.addEventListener('message', handlePgwState);
    return () => window.removeEventListener('message', handlePgwState);
  }, []);

  const { mutate: setPin, isLoading: isLoadingSetPin } = useMutation(CARD_MONAVATE_SET_PIN, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else if (data) {
        showAlert({
          message: translate('set_pin_success_msg'),
          type: 'success',
        });
        history.push('/dashboard/card');
      } else {
        showAlert({
          message: translate('generic_error_msg'),
          type: 'danger',
        });
      }
    },
  });

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    updateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    if (e.target.value) {
      if (e.target.name === 'pin') {
        if (
          Number.isNaN(e.target.value) ||
          e.target.value.length < 4 ||
          e.target.value.length > 4
        ) {
          if (e.target.className.indexOf('has-error') === -1) {
            e.target.className += ' has-error';
          }
        } else {
          clearFormError(e);
        }
      } else if (e.target.name === 'confirmPin') {
        if (
          Number.isNaN(e.target.value) ||
          e.target.value !== form.pin ||
          e.target.value.length < 4 ||
          e.target.value.length > 4
        ) {
          if (e.target.className.indexOf('has-error') === -1) {
            e.target.className += ' has-error';
          }
        } else {
          clearFormError(e);
        }
      }
    }
  };
  const handleSubmit = () => {
    setPin({ pin: form.pin, existingPin: currentPIN });
  };

  if (cardProvider === cardProviders.GALILEO) {
    return (
      <MainBodyLayout
        withBack
        onPrevClick={() => openScreen(cardSettingsScreens.VERIFY_PASSWORD)}
        title={translate('manage_card_setting_option_set_pin_title')}>
        <p className="text-f5 text-dark mt-4">{translate('set_pin_label_galileo')}</p>
        {(!changePinUrl || pinUrlLoading) && <GenericLoading isFullScreen={false} />}
        <div className="iframe-container">
          <iframe
            hidden={pinUrlLoading}
            onLoad={() => {
              const timer = setTimeout(() => {
                setPinUrlLoading(false);
              }, 2000);
              return () => clearTimeout(timer);
            }}
            className="w-100"
            title="PGW Url"
            src={changePinUrl}
          />
        </div>
      </MainBodyLayout>
    );
  }

  return (
    <MainBodyLayout
      withBack
      onPrevClick={() => openScreen(cardSettingsScreens.VERIFY_PASSWORD)}
      title={translate('manage_card_setting_option_set_pin_title')}>
      <p className="text-f5 text-dark mt-4">{translate('set_pin_label')}</p>
      <Input
        type="password"
        groupClassName="mt-4"
        placeholder={translate('enter_desired_pin_label')}
        label={translate('enter_desired_pin_label')}
        errorLabel={translate('set_pin_input_error_label')}
        name="pin"
        value={form.pin}
        max={4}
        onBlur={handleBlur}
        onChange={handleChange}
        required
        withFloatingLabel
        withErrorLabel
        inputGroupChildren={
          <>
            <span
              className="icon icon-open-icon h4 text-shadow"
              role="button"
              onClick={revealPassword}
              onKeyDown={null}
              tabIndex={-1}
              aria-label="Reveal password"
            />
          </>
        }
      />
      <Input
        type="password"
        groupClassName="mt-0"
        placeholder={translate('confirm_desired_pin_label')}
        label={translate('confirm_desired_pin_label')}
        errorLabel={
          form.pin !== form.confirmPin
            ? translate('confirm_pin_input_error_label')
            : translate('set_pin_input_error_label')
        }
        name="confirmPin"
        value={form.confirmPin}
        max={4}
        onBlur={handleBlur}
        onChange={handleChange}
        required
        withFloatingLabel
        withErrorLabel
        inputGroupChildren={
          <>
            <span
              className="icon icon-open-icon h4 text-shadow"
              role="button"
              onClick={revealPassword}
              onKeyDown={null}
              tabIndex={-1}
              aria-label="Reveal password"
            />
          </>
        }
      />
      <div
        className={`d-flex flex-row mt-5 pt-3 ${
          isMobile ? 'position-fixed w-95 bg-light pb-3 pe-2' : 'mx-2 px-5'
        }`}
        style={{ bottom: '0', left: '0', zIndex: 100 }}>
        <button
          disabled={
            !form.pin ||
            form.pin.length > 4 ||
            form.pin.length < 4 ||
            Number.isNaN(form.pin) ||
            Number.isNaN(form.confirmPin) ||
            !form.confirmPin ||
            form.confirmPin.length > 4 ||
            form.confirmPin.length < 4 ||
            form.pin !== form.confirmPin
          }
          loading={isLoadingSetPin ? 'true' : 'false'}
          type="button"
          className="btn py-3 btn-primary w-100"
          onClick={handleSubmit}>
          {translate('continue_btn_title')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default SetPin;
